import React, { useRef } from 'react';

import HeaderLink from '../NewHeader/HeaderLink';
import Icon from '../Icon';
import { LangSwitcher } from '../LangSwitcher/LangSwitcher';
import { shouldOpenCtaInNewTab } from '../../utils/utils';

import * as styles from './NavBarMobile.module.scss';

function NavBarMobile({
  navBarData,
  iconsMap,
  handleMenuLinkClick,
  safeFlags,
  locale,
}) {
  const navRef = useRef(null);
  const langSwitcherCloseRef = useRef(null);

  if (!navBarData) {
    return null;
  }

  const renderDetailLink = (key, link, label) => (
    <li key={key}>
      <HeaderLink
        link={link}
        className={styles.menuModalContent_detailLink}
        onClick={handleMenuLinkClick}
      >
        {label}
      </HeaderLink>
    </li>
  );

  const closeOpenDetails = () => {
    const navElement = navRef.current;
    if (!navElement) return;
    navElement.querySelectorAll('details[open]').forEach((detail) => {
      detail.open = false;
    });
  };

  const handleLangSwitcherToggle = (nextOpen) => {
    if (nextOpen) {
      closeOpenDetails();
    }
  };

  const handleRegisterLangSwitcherClose = (closeFn) => {
    langSwitcherCloseRef.current = closeFn;
  };

  const handleDetailsToggle = (event) => {
    if (event.currentTarget.open) {
      langSwitcherCloseRef.current?.();
    }
  };

  const needsGroup = navBarData.needs_group ?? [];
  const sectorsGroup = navBarData.sectors_group ?? [];
  const linksGroup = navBarData.links_group ?? [];
  const body = navBarData.body ?? [];

  return (
    <nav
      className={styles.menuModalContent}
      aria-label="Navigation mobile principale"
      ref={navRef}
    >
      <h2 id="mobile-menu-title" className={styles.srOnly}>
        Menu
      </h2>
      <details
        className={styles.menuModalContent_detailMain}
        onToggle={handleDetailsToggle}
      >
        <summary className={styles.menuModalContent_title}>
          {navBarData.solution_group_name}
          <span className={styles.menuModalContent_iconSwap}>
            <Icon
              name="left"
              iconsMap={iconsMap}
              className={styles.menuModalContent_icon}
            />
          </span>
        </summary>
        <ul>
          <li>
            <details
              className={styles.menuModalContent_detailSub}
              onToggle={handleDetailsToggle}
            >
              <summary className={styles.menuModalContent_subtitle}>
                {navBarData.needs_group_name}
                <Icon
                  name="left"
                  iconsMap={iconsMap}
                  className={styles.menuModalContent_iconSub}
                />
              </summary>
              <ul className={styles.menuModalContent_detailList}>
                {needsGroup.map((need) => {
                  return renderDetailLink(
                    need.needs_link_id,
                    need.needs_link,
                    need.needs_link_name
                  );
                })}
              </ul>
            </details>
          </li>
          <li>
            <details
              className={styles.menuModalContent_detailSub}
              onToggle={handleDetailsToggle}
            >
              <summary className={styles.menuModalContent_subtitle}>
                {navBarData.sectors_group_name}
                <Icon
                  name="left"
                  iconsMap={iconsMap}
                  className={styles.menuModalContent_iconSub}
                />
              </summary>
              <ul className={styles.menuModalContent_detailList}>
                {sectorsGroup.map((sector) => {
                  return renderDetailLink(
                    sector.sectors_link_id,
                    sector.sectors_link,
                    sector.sectors_link_name
                  );
                })}
              </ul>
            </details>
          </li>
          <li>
            <ul className={styles.menuModalContent_detailList}>
              {linksGroup.map((link) => {
                return renderDetailLink(
                  link.group_link_id,
                  link.group_link,
                  link.group_link_name
                );
              })}
            </ul>
          </li>
        </ul>
      </details>
      <ul className={styles.menuModalContent_rootList}>
        {body.map((navlink, index) => {
          const primary = navlink?.primary ?? {};
          const baseKey =
            primary.group_id || primary.group_name || `slice-${index}`;
          if (
            primary.link_name?.url ||
            primary.link_name?.uid ||
            !navlink.items?.length
          ) {
            const itemKey =
              primary.link_name?.uid ||
              primary.group_id ||
              primary.group_name ||
              `slice-${index}`;
            return (
              <li key={itemKey}>
                <HeaderLink
                  link={primary.link_name}
                  className={styles.menuModalContent_title}
                  onClick={handleMenuLinkClick}
                >
                  {primary.group_name}
                </HeaderLink>
              </li>
            );
          }

          if (primary.group_id === 'ia') {
            const items = navlink.items ?? [];
            return (
              <li key={baseKey}>
                <details
                  className={styles.menuModalContent_detailMain}
                  onToggle={handleDetailsToggle}
                >
                  <summary className={styles.menuModalContent_title}>
                    <span className={styles.menuModalContent_titleIa}>
                      <Icon
                        name="ia"
                        iconsMap={iconsMap}
                        className={styles.menuModalContent_iconIa}
                      />
                      {primary.group_name}
                    </span>
                    <Icon
                      name="left"
                      iconsMap={iconsMap}
                      className={styles.menuModalContent_icon}
                    />
                  </summary>
                  <ul className={styles.menuModalContent_detailList}>
                    {items.map((link) => {
                      return renderDetailLink(
                        link.link_id,
                        link.navigation_link,
                        link.link_name
                      );
                    })}
                  </ul>
                </details>
              </li>
            );
          }

          if (primary.group_id === 'cta') {
            const items = navlink.items ?? [];
            return (
              <li key={baseKey}>
                <span id="cta-actions-label" className={styles.srOnly}>
                  Actions
                </span>
                <ul
                  className={styles.menuModalCtaList}
                  aria-labelledby="cta-actions-label"
                >
                  {items.map((button) => {
                    const buttonUrl = button?.navigation_link?.url;
                    const openInNewTab = shouldOpenCtaInNewTab(
                      button.link_id,
                      button.link_name
                    );
                    const isSecondary = button.link_id === 'connection';
                    const buttonClassName = `${styles.menuModalCtaButton} ${
                      isSecondary
                        ? styles.menuModalCtaButtonSecondary
                        : styles.menuModalCtaButtonPrimary
                    }`;

                    return (
                      <li
                        key={button.link_id}
                        className={styles.menuModalCtaItem}
                      >
                        <a
                          className={buttonClassName}
                          href={buttonUrl}
                          rel="noopener noreferrer nofollow"
                          target={openInNewTab ? '_blank' : undefined}
                          onClick={handleMenuLinkClick}
                        >
                          {button.link_name}
                        </a>
                      </li>
                    );
                  })}
                  <li
                    className={`${styles.menuModalCtaItem} ${styles.menuModalLangSwitcherItem}`}
                  >
                    <LangSwitcher
                      flags={safeFlags}
                      localeInfo={locale}
                      onRegisterClose={handleRegisterLangSwitcherClose}
                      onToggle={handleLangSwitcherToggle}
                    />
                  </li>
                </ul>
              </li>
            );
          }

          const items = navlink.items ?? [];
          return (
            <li key={baseKey}>
              <details
                className={styles.menuModalContent_detailMain}
                onToggle={handleDetailsToggle}
              >
                <summary className={styles.menuModalContent_title}>
                  {primary.group_name}
                  <Icon
                    name="left"
                    iconsMap={iconsMap}
                    className={styles.menuModalContent_icon}
                  />
                </summary>
                <ul className={styles.menuModalContent_detailList}>
                  {items.map((link) => {
                    return renderDetailLink(
                      link.link_id,
                      link.navigation_link,
                      link.link_name
                    );
                  })}
                </ul>
              </details>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default NavBarMobile;
